import { BrandThemeProvider } from '@montugroup/themes';
import { useLocation, useNavigate } from 'react-router-dom';

import { OrderSuccess } from '@/components/escript/order/order-success/OrderSuccess';
import OrderBreakdown, { getOrderBreakdownProps } from '@/components/order/order-breakdown/OrderBreakdown';
import { OrderBreakdownLoading } from '@/components/order/order-breakdown/OrderBreakdownLoading';
import type { OrderStatus } from '@/components/order/OrderStatusChip/OrderStatusChip';
import { getOrderStatusChipProps } from '@/components/order/OrderStatusChip/OrderStatusChip';
import type { OrderStage } from '@/components/order/pharmacy-order-details/PharmacyOrderDetails';
import PharmacyOrderDetails from '@/components/order/pharmacy-order-details/PharmacyOrderDetails';
import PharmacyOrderDetailsLoading from '@/components/order/pharmacy-order-details/PharmacyOrderDetailsLoading';
import type { OrderDetails } from '@/hooks/order/useGetOrderDetails';
import type { PatientOrder } from '@/hooks/patient/useGetPatientOrders';
import useGetPatientOrders from '@/hooks/patient/useGetPatientOrders';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { PATIENT_ORDERS_ROUTE } from '@/routes/route-paths';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { useEffect, useState } from 'react';
import { OrderSuccessDetailsContainer, OrderSumaryContainer, PharmacyContainer } from './OrderSuccess.styles';

export const EscriptOrderSuccess = () => {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { key: partnerKey } = usePortalPartnerBrand();
  const { data: orders, isLoading } = useGetPatientOrders();
  const [orderDetails, setOrderDetails] = useState<PatientOrder | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const { orderCode } = location.state || {};

  useEffect(() => {
    if (!isLoading && orders) {
      setHasAttemptedLoad(true);
      for (const order of orders) {
        if (order.order_code === orderCode) {
          setOrderDetails(order);
          setOrderStatus(
            getOrderStatusChipProps({
              status: order.orderStatus,
              delivered: order.delivered,
            }).status ?? null,
          );
          break;
        }
      }
    }
  }, [orders, orderCode, isLoading]);

  if (!orderCode || (hasAttemptedLoad && !isLoading && !orderDetails)) {
    navigate(PATIENT_ORDERS_ROUTE);
  }

  return (
    <BrandThemeProvider theme={partnerKey}>
      <OrderSuccess orderId={orderCode} email={user?.email} status="success" />
      <OrderSuccessDetailsContainer>
        <PharmacyContainer>
          {isLoading ? (
            <PharmacyOrderDetailsLoading testId="pharmacy-order-details-loading" />
          ) : (
            orderDetails && (
              <PharmacyOrderDetails
                pharmacy={orderDetails.pharmacy}
                fullOrderCode={orderDetails.order_code}
                stage={orderStatus as OrderStage}
                deliveryOption={orderDetails.deliveryOption}
              />
            )
          )}
        </PharmacyContainer>
        <OrderSumaryContainer>
          {isLoading ? (
            <OrderBreakdownLoading testId="order-breakdown-loading" />
          ) : (
            orderDetails && (
              <OrderBreakdown
                headerText="Order summary"
                {...getOrderBreakdownProps({
                  dispensingFee: Number(orderDetails.dispensing_fee),
                  orderDetails: [orderDetails as unknown as OrderDetails],
                })}
              />
            )
          )}
        </OrderSumaryContainer>
      </OrderSuccessDetailsContainer>
    </BrandThemeProvider>
  );
};
